import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import MainHeader from '../../components/MainHeader';
import Footer from '../../components/Footer';
import './RewardsScreen.css';

export default function RewardsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rewards, setRewards] = useState(null);
  const [badges, setBadges] = useState([]);
  const [earnedCount, setEarnedCount] = useState(0);
  const [pendingVisits, setPendingVisits] = useState([]);
  const [cashbackSummary, setCashbackSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPremium = user?.is_premium || ['active', 'trialing'].includes(user?.subscription_status);

  const loadRewards = useCallback(async () => {
    try {
      const [rewardsRes, badgesRes] = await Promise.all([
        api.get('/rewards/summary'),
        api.get('/rewards/badges'),
      ]);
      setRewards(rewardsRes.data.rewards);
      setBadges(badgesRes.data.badges || []);
      setEarnedCount(badgesRes.data.earnedCount || 0);

      if (isPremium) {
        try {
          const visitsRes = await api.get('/visits/pending');
          setPendingVisits(visitsRes.data.verifications || []);
        } catch { /* non-premium will 403 */ }

        // Load cashback balance for premium users
        try {
          const cbRes = await api.get('/cashback/balance');
          setCashbackSummary(cbRes.data || null);
        } catch {
          setCashbackSummary(null);
        }
      }
    } catch (err) {
      console.error('Failed to load rewards:', err);
    } finally {
      setLoading(false);
    }
  }, [isPremium]);

  useEffect(() => {
    loadRewards();
  }, [loadRewards]);

  function getLevelName(level) {
    if (level <= 5) return 'Hungry Newbie';
    if (level <= 10) return 'Foodie Explorer';
    if (level <= 15) return 'Hangry Hero';
    return 'Culinary Legend';
  }

  function getNextLevelPoints(level) {
    if (level === 1) return 500;
    if (level === 2) return 1000;
    if (level === 3) return 1500;
    if (level === 4) return 2000;
    if (level === 5) return 3000;
    if (level === 6) return 4000;
    if (level === 7) return 5000;
    if (level === 8) return 7500;
    if (level === 9) return 10000;
    return 10000 + (level - 9) * 2500;
  }

  if (loading) {
    return (
      <div className="main-screen">
        <MainHeader title="Rewards" showBack={true} backPath="/home" />
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading rewards...</p>
        </div>
      </div>
    );
  }

  const totalPoints = rewards?.total_points || 0;
  const currentPoints = rewards?.current_points || 0;
  const level = rewards?.level || 1;
  const nextLevelPoints = getNextLevelPoints(level);
  const levelProgress = Math.min((totalPoints / nextLevelPoints) * 100, 100);

  return (
    <div className="main-screen">
      <MainHeader title="Rewards" showBack={true} backPath="/home" />

      <main className="screen-main">
        <div className="rewards-container">

          {/* Points Card */}
          <div className="rw-points-card">
            <div className="rw-points-header">
              <div>
                <div className="rw-points-label">Hangry Points</div>
                <div className="rw-points-value">{currentPoints.toLocaleString()}</div>
              </div>
              <span className="rw-points-emoji">🎯</span>
            </div>
            <div className="rw-level-section">
              <div className="rw-level-row">
                <span className="rw-level-text">Level {level}</span>
                <span className="rw-level-name">{getLevelName(level)}</span>
              </div>
              <div className="rw-progress-bar">
                <div className="rw-progress-fill" style={{ width: `${levelProgress}%` }}></div>
              </div>
              <div className="rw-progress-text">
                {totalPoints.toLocaleString()} / {nextLevelPoints.toLocaleString()} points to Level {level + 1}
              </div>
            </div>
            {rewards?.current_streak > 0 && (
              <div className="rw-streak-badge">🔥 {rewards.current_streak} day streak!</div>
            )}
          </div>

          {/* Cashback Earnings */}
          {cashbackSummary && (
            <div className="rw-cashback-card">
              <div className="rw-cashback-header">
                <span className="rw-cashback-emoji">💰</span>
                <div>
                  <div className="rw-cashback-label">Cashback Earnings</div>
                  <div className="rw-cashback-value">
                    ${((cashbackSummary.confirmed_cents + cashbackSummary.paid_cents) / 100).toFixed(2)} earned
                  </div>
                </div>
              </div>
              
              {/* Monthly Limit Progress */}
              {cashbackSummary.monthly_limit && (
                <div style={{ marginBottom: '16px', padding: '12px', background: '#f8f8f8', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#666' }}>
                      This Month's Cashback Visits
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: cashbackSummary.monthly_remaining > 0 ? '#06C167' : '#FF6347' }}>
                      {cashbackSummary.monthly_used} / {cashbackSummary.monthly_limit}
                    </span>
                  </div>
                  <div style={{ width: '100%', height: '8px', background: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(cashbackSummary.monthly_used / cashbackSummary.monthly_limit) * 100}%`,
                      height: '100%',
                      background: cashbackSummary.monthly_remaining > 0 ? '#06C167' : '#FF6347',
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '8px 0 0 0' }}>
                    {cashbackSummary.monthly_remaining > 0 
                      ? `${cashbackSummary.monthly_remaining} cashback visit${cashbackSummary.monthly_remaining === 1 ? '' : 's'} remaining`
                      : 'Limit reached. Resets next month.'}
                  </p>
                </div>
              )}

              <div className="rw-cashbreakdown">
                <div className="rw-cb-item">
                  <span className="rw-cb-dot" style={{ background: '#FFD700' }}></span>
                  <span className="rw-cb-label">Pending</span>
                  <span className="rw-cb-amount">${(cashbackSummary.pending_cents / 100).toFixed(2)}</span>
                </div>
                <div className="rw-cb-item">
                  <span className="rw-cb-dot" style={{ background: '#06C167' }}></span>
                  <span className="rw-cb-label">Confirmed</span>
                  <span className="rw-cb-amount">${(cashbackSummary.confirmed_cents / 100).toFixed(2)}</span>
                </div>
                <div className="rw-cb-item">
                  <span className="rw-cb-dot" style={{ background: '#4CAF50' }}></span>
                  <span className="rw-cb-label">Paid</span>
                  <span className="rw-cb-amount">${(cashbackSummary.paid_cents / 100).toFixed(2)}</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/redeem')}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginTop: '12px',
                  background: 'linear-gradient(135deg, #06C167, #4CAF50)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                💸 Cash Out
              </button>
              <p className="rw-cashback-disclaimer">
                Earn $1 cashback per verified order (up to 10/month). Auto-approved when your receipt + location check out.
              </p>
            </div>
          )}

          {/* Action Buttons or Premium Upsell */}
          {isPremium ? (
            <div className="rw-action-row">
              <button className="rw-action-card" onClick={() => navigate('/rewards/spin')}>
                <span className="rw-action-emoji">🎰</span>
                <span className="rw-action-title">Spin & Win</span>
                <span className="rw-action-sub">Free daily spin!</span>
              </button>
              <button className="rw-action-card" onClick={() => navigate('/rewards/store')}>
                <span className="rw-action-emoji">🛍️</span>
                <span className="rw-action-title">Reward Store</span>
                <span className="rw-action-sub">Spend your points</span>
              </button>
            </div>
          ) : (
            <button className="rw-premium-upsell" onClick={() => navigate('/subscription')}>
              <span className="rw-upsell-emoji">⭐</span>
              <div className="rw-upsell-text">
                <div className="rw-upsell-title">Unlock Rewards with Premium</div>
                <div className="rw-upsell-sub">
                  Spin the wheel, shop the store, earn 2x points, and review restaurants!
                </div>
              </div>
              <span className="rw-upsell-arrow">→</span>
            </button>
          )}

          {/* Pending Visit Reviews */}
          {isPremium && pendingVisits.length > 0 && (
            <div className="rw-section">
              <h3 className="rw-section-title">📝 Pending Reviews</h3>
              {pendingVisits.slice(0, 3).map(visit => (
                <div key={visit.id} className="rw-pending-card">
                  <span className="rw-pending-emoji">
                    {visit.visitStatus === 'unconfirmed' ? '🍽️' : '⭐'}
                  </span>
                  <div className="rw-pending-info">
                    <div className="rw-pending-name">
                      {visit.final_restaurant?.name || 'Restaurant'}
                    </div>
                    <div className="rw-pending-sub">
                      {visit.visitStatus === 'unconfirmed' ? 'Did you go? +25 pts' : 'Leave a review! +35 pts'}
                    </div>
                  </div>
                  <span className="rw-pending-arrow">→</span>
                </div>
              ))}
            </div>
          )}

          {/* How to Earn Points */}
          <div className="rw-section">
            <h3 className="rw-section-title">How to Earn Points</h3>
            <div className="rw-earn-list">
              {[
                { emoji: '🍽️', title: 'Make a Decision', sub: '10 points' },
                { emoji: '✅', title: 'Confirm You Visited', sub: '25 points' },
                { emoji: '⭐', title: 'Rate Your Experience', sub: '+10 bonus points' },
                { emoji: '📝', title: 'Write a Review', sub: '+15 bonus points' },
                { emoji: '💑', title: 'Couple Decision', sub: '15 points' },
                { emoji: '👥', title: 'Group Session', sub: '20 points' },
                { emoji: '🔥', title: 'Daily Streak', sub: '5 points per day' },
                { emoji: '🎰', title: 'Spin the Wheel', sub: 'Win up to 250 points!' },
              ].map((item, i) => (
                <div key={i} className="rw-earn-card">
                  <span className="rw-earn-emoji">{item.emoji}</span>
                  <div className="rw-earn-text">
                    <div className="rw-earn-title">{item.title}</div>
                    <div className="rw-earn-sub">{item.sub}</div>
                  </div>
                </div>
              ))}
              <div className="rw-earn-card rw-earn-premium">
                <span className="rw-earn-emoji">⭐</span>
                <div className="rw-earn-text">
                  <div className="rw-earn-title">Premium Members</div>
                  <div className="rw-earn-sub" style={{ color: '#FFD43B' }}>2x points on everything!</div>
                </div>
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="rw-section">
            <div className="rw-section-header">
              <h3 className="rw-section-title">Badges</h3>
              <span className="rw-badge-count">{earnedCount} / {badges.length}</span>
            </div>
            <div className="rw-badges-grid">
              {badges.map(badge => (
                <div key={badge.id} className={`rw-badge-card ${!badge.earned ? 'rw-badge-locked' : ''}`}>
                  <span className="rw-badge-emoji">{badge.emoji}</span>
                  <div className="rw-badge-name">{badge.name}</div>
                  <div className="rw-badge-req">{badge.requirement}</div>
                  {badge.earned && <div className="rw-badge-earned">✓ Earned</div>}
                </div>
              ))}
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
